import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Alert,
  Autocomplete,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Preview as PreviewIcon,
  Save as SaveIcon,
  Calculate as CalculateIcon,
  Map as MapIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';

// Database tables and views from Zanzibar database
const DATABASE_VIEWS = [
  { value: 'enrolment_by_level', label: 'Enrollment by Education Level', category: 'enrollment' },
  { value: 'enrolment_net', label: 'Net Enrollment (Age-Appropriate)', category: 'enrollment' },
  { value: 'enrolment_age_type_by_level', label: 'Enrollment by Age Category', category: 'enrollment' },
  { value: 'enrolment_repeaters_by_level', label: 'Repeaters by Level', category: 'enrollment' },
  { value: 'enrolment_dropouts_by_level', label: 'Dropouts by Level', category: 'enrollment' },
  { value: 'enrolment_disabilities', label: 'Students with Disabilities', category: 'enrollment' },
  { value: 'schools_active_by_level', label: 'Active Schools by Level', category: 'infrastructure' },
  { value: 'classrooms_functional', label: 'Functional Classrooms', category: 'infrastructure' },
  { value: 'toilets_functional', label: 'Functional Toilets', category: 'infrastructure' },
  { value: 'seats_functional', label: 'Functional Seating', category: 'infrastructure' },
  { value: 'facilities', label: 'School Facilities', category: 'infrastructure' },
  { value: 'computers', label: 'Computer Equipment', category: 'infrastructure' },
  { value: 'exam_attendance', label: 'Exam Attendance', category: 'academic' },
  { value: 'exam_total_score', label: 'Exam Scores', category: 'academic' },
  { value: 'population_by_level', label: 'School-Age Population', category: 'demographic' },
  { value: 'staff_qualification', label: 'Staff Qualifications', category: 'staff' },
  { value: 'staff_allocation', label: 'Staff Allocation', category: 'staff' },
];

const AGGREGATION_FUNCTIONS = [
  { value: 'SUM', label: 'Sum' },
  { value: 'COUNT', label: 'Count' },
  { value: 'AVG', label: 'Average' },
  { value: 'MAX', label: 'Maximum' },
  { value: 'MIN', label: 'Minimum' },
  { value: 'DISTINCT_COUNT', label: 'Count Distinct' },
];

const FILTER_CONDITIONS = [
  { value: '=', label: 'Equals' },
  { value: '!=', label: 'Not Equals' },
  { value: '>', label: 'Greater Than' },
  { value: '>=', label: 'Greater Than or Equal' },
  { value: '<', label: 'Less Than' },
  { value: '<=', label: 'Less Than or Equal' },
  { value: 'IN', label: 'In List' },
  { value: 'LIKE', label: 'Contains' },
];

const GROUPING_LEVELS = [
  { value: 'school', label: 'School Level' },
  { value: 'shehia', label: 'Shehia Level' },
  { value: 'ward', label: 'Ward Level' },
  { value: 'constituency', label: 'Constituency Level' },
  { value: 'district', label: 'District Level' },
  { value: 'region', label: 'Region Level' },
  { value: 'island', label: 'Island Level' },
  { value: 'nation', label: 'National Level' },
];

interface IndicatorMapping {
  sourceTable: string;
  sourceField: string;
  aggregation: string;
  filters: Array<{
    field: string;
    condition: string;
    value: string;
  }>;
  groupBy: string[];
}

interface IndicatorDefinition {
  code: string;
  name: string;
  description: string;
  category: string;
  dataType: string;
  unit: string;
  mappings: IndicatorMapping[];
  calculationFormula: string;
  isCalculated: boolean;
}

const IndicatorBuilder: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [indicator, setIndicator] = useState<IndicatorDefinition>({
    code: '',
    name: '',
    description: '',
    category: 'enrollment',
    dataType: 'integer',
    unit: '',
    mappings: [],
    calculationFormula: '',
    isCalculated: false,
  });

  const steps = [
    'Basic Information',
    'Data Mapping',
    'Calculation Rules',
    'Preview & Save'
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const addMapping = () => {
    setIndicator(prev => ({
      ...prev,
      mappings: [...prev.mappings, {
        sourceTable: '',
        sourceField: '',
        aggregation: 'SUM',
        filters: [],
        groupBy: ['school']
      }]
    }));
  };

  const updateMapping = (index: number, field: keyof IndicatorMapping, value: any) => {
    setIndicator(prev => ({
      ...prev,
      mappings: prev.mappings.map((mapping, i) => 
        i === index ? { ...mapping, [field]: value } : mapping
      )
    }));
  };

  const addFilter = (mappingIndex: number) => {
    setIndicator(prev => ({
      ...prev,
      mappings: prev.mappings.map((mapping, i) => 
        i === mappingIndex 
          ? { ...mapping, filters: [...mapping.filters, { field: '', condition: '=', value: '' }] }
          : mapping
      )
    }));
  };

  const generateSQL = () => {
    const { mappings } = indicator;
    if (mappings.length === 0) return '';

    const mapping = mappings[0]; // For simplicity, show first mapping
    const { sourceTable, sourceField, aggregation, filters, groupBy } = mapping;

    let sql = `SELECT\n`;
    
    // Add grouping fields
    groupBy.forEach(group => {
      sql += `  ${group}_name,\n`;
    });
    
    // Add aggregated field
    sql += `  ${aggregation}(${sourceField}) as ${indicator.code.toLowerCase()}_value\n`;
    sql += `FROM ${sourceTable}\n`;
    
    // Add joins for hierarchy
    if (groupBy.includes('district') || groupBy.includes('region')) {
      sql += `JOIN schools_district_hierarchy h ON ${sourceTable}.school_id = h.school_id\n`;
    }
    
    // Add filters
    if (filters.length > 0) {
      sql += `WHERE\n`;
      filters.forEach((filter, index) => {
        sql += `  ${filter.field} ${filter.condition} '${filter.value}'`;
        if (index < filters.length - 1) sql += ' AND\n';
        else sql += '\n';
      });
    }
    
    // Add grouping
    sql += `GROUP BY ${groupBy.map(g => `${g}_name`).join(', ')}\n`;
    sql += `ORDER BY ${groupBy.map(g => `${g}_name`).join(', ')}`;
    
    return sql;
  };

  const getFieldsForTable = (tableName: string) => {
    const fieldMap: Record<string, string[]> = {
      'enrolment_by_level': ['student_count', 'gender', 'education_level_id', 'year'],
      'enrolment_net': ['net_enrollment', 'gender', 'education_level_id', 'year'],
      'classrooms_functional': ['classroom_count', 'capacity', 'year'],
      'toilets_functional': ['toilet_count', 'gender', 'year'],
      'facilities': ['facility_id', 'has_facility', 'year'],
      'exam_attendance': ['attended_count', 'total_count', 'year'],
      'staff_allocation': ['staff_count', 'qualification_id', 'position_id', 'year'],
    };
    return fieldMap[tableName] || [];
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Custom Indicator Builder
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Create custom indicators by mapping data from Zanzibar's education database views
      </Typography>

      <Stepper activeStep={activeStep} orientation="vertical">
        {/* Step 1: Basic Information */}
        <Step>
          <StepLabel>Basic Information</StepLabel>
          <StepContent>
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Indicator Code"
                      value={indicator.code}
                      onChange={(e) => setIndicator(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                      placeholder="ENR_PRIMARY_RATE"
                      helperText="Unique identifier using uppercase letters and underscores"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Indicator Name"
                      value={indicator.name}
                      onChange={(e) => setIndicator(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Primary School Enrollment Rate"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Description"
                      value={indicator.description}
                      onChange={(e) => setIndicator(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe what this indicator measures and how it should be interpreted"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      select
                      label="Category"
                      value={indicator.category}
                      onChange={(e) => setIndicator(prev => ({ ...prev, category: e.target.value }))}
                    >
                      <MenuItem value="enrollment">Enrollment</MenuItem>
                      <MenuItem value="infrastructure">Infrastructure</MenuItem>
                      <MenuItem value="academic">Academic Performance</MenuItem>
                      <MenuItem value="staff">Staff</MenuItem>
                      <MenuItem value="finance">Financial</MenuItem>
                      <MenuItem value="demographic">Demographics</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      select
                      label="Data Type"
                      value={indicator.dataType}
                      onChange={(e) => setIndicator(prev => ({ ...prev, dataType: e.target.value }))}
                    >
                      <MenuItem value="integer">Integer</MenuItem>
                      <MenuItem value="decimal">Decimal</MenuItem>
                      <MenuItem value="percentage">Percentage</MenuItem>
                      <MenuItem value="ratio">Ratio</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Unit"
                      value={indicator.unit}
                      onChange={(e) => setIndicator(prev => ({ ...prev, unit: e.target.value }))}
                      placeholder="students, %, ratio"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!indicator.code || !indicator.name}
              >
                Next
              </Button>
            </Box>
          </StepContent>
        </Step>

        {/* Step 2: Data Mapping */}
        <Step>
          <StepLabel>Data Mapping</StepLabel>
          <StepContent>
            <Box sx={{ mb: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Map your indicator to data sources from Zanzibar's education database views
              </Alert>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addMapping}
              >
                Add Data Source
              </Button>
            </Box>

            {indicator.mappings.map((mapping, index) => (
              <Card key={index} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Data Source {index + 1}</Typography>
                    <IconButton
                      color="error"
                      onClick={() => setIndicator(prev => ({
                        ...prev,
                        mappings: prev.mappings.filter((_, i) => i !== index)
                      }))}
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        options={DATABASE_VIEWS}
                        groupBy={(option) => option.category}
                        getOptionLabel={(option) => option.label}
                        value={DATABASE_VIEWS.find(v => v.value === mapping.sourceTable) || null}
                        onChange={(_, value) => updateMapping(index, 'sourceTable', value?.value || '')}
                        renderInput={(params) => (
                          <TextField {...params} label="Source Table/View" fullWidth />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        select
                        label="Field"
                        value={mapping.sourceField}
                        onChange={(e) => updateMapping(index, 'sourceField', e.target.value)}
                        disabled={!mapping.sourceTable}
                      >
                        {getFieldsForTable(mapping.sourceTable).map(field => (
                          <MenuItem key={field} value={field}>{field}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        select
                        label="Aggregation"
                        value={mapping.aggregation}
                        onChange={(e) => updateMapping(index, 'aggregation', e.target.value)}
                      >
                        {AGGREGATION_FUNCTIONS.map(func => (
                          <MenuItem key={func.value} value={func.value}>{func.label}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {/* Grouping */}
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        options={GROUPING_LEVELS}
                        getOptionLabel={(option) => option.label}
                        value={GROUPING_LEVELS.filter(level => mapping.groupBy.includes(level.value))}
                        onChange={(_, values) => updateMapping(index, 'groupBy', values.map(v => v.value))}
                        renderInput={(params) => (
                          <TextField {...params} label="Group By" placeholder="Select administrative levels" />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, i) => {
                            const { key, ...tagProps } = getTagProps({ index: i });
                            return (
                              <Chip key={option.value} label={option.label} {...tagProps} />
                            );
                          })
                        }
                      />
                    </Grid>

                    {/* Filters */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">Filters</Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => addFilter(index)}
                        >
                          Add Filter
                        </Button>
                      </Box>
                      {mapping.filters.map((filter, filterIndex) => (
                        <Grid container spacing={1} key={filterIndex} sx={{ mb: 1 }}>
                          <Grid item xs={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Field"
                              value={filter.field}
                              onChange={(e) => {
                                const newFilters = [...mapping.filters];
                                newFilters[filterIndex].field = e.target.value;
                                updateMapping(index, 'filters', newFilters);
                              }}
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <TextField
                              fullWidth
                              select
                              size="small"
                              label="Condition"
                              value={filter.condition}
                              onChange={(e) => {
                                const newFilters = [...mapping.filters];
                                newFilters[filterIndex].condition = e.target.value;
                                updateMapping(index, 'filters', newFilters);
                              }}
                            >
                              {FILTER_CONDITIONS.map(cond => (
                                <MenuItem key={cond.value} value={cond.value}>{cond.label}</MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Value"
                              value={filter.value}
                              onChange={(e) => {
                                const newFilters = [...mapping.filters];
                                newFilters[filterIndex].value = e.target.value;
                                updateMapping(index, 'filters', newFilters);
                              }}
                            />
                          </Grid>
                          <Grid item xs={1}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const newFilters = mapping.filters.filter((_, i) => i !== filterIndex);
                                updateMapping(index, 'filters', newFilters);
                              }}
                            >
                              <RemoveIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}

            <Box sx={{ mt: 2 }}>
              <Button onClick={handleBack} sx={{ mr: 1 }}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={indicator.mappings.length === 0}
              >
                Next
              </Button>
            </Box>
          </StepContent>
        </Step>

        {/* Step 3: Calculation Rules */}
        <Step>
          <StepLabel>Calculation Rules</StepLabel>
          <StepContent>
            <Card>
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={indicator.isCalculated}
                      onChange={(e) => setIndicator(prev => ({ ...prev, isCalculated: e.target.checked }))}
                    />
                  }
                  label="This is a calculated indicator"
                />

                {indicator.isCalculated && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Calculation Formula"
                      value={indicator.calculationFormula}
                      onChange={(e) => setIndicator(prev => ({ ...prev, calculationFormula: e.target.value }))}
                      placeholder="e.g., (primary_enrollment / school_age_population) * 100"
                      helperText="Use mathematical expressions to combine multiple data sources"
                    />
                    <Alert severity="info" sx={{ mt: 2 }}>
                      You can reference other indicators by their codes and use mathematical operators (+, -, *, /, %).
                      Example: (TOTAL_ENROLLED / SCHOOL_AGE_POP) * 100
                    </Alert>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Box sx={{ mt: 2 }}>
              <Button onClick={handleBack} sx={{ mr: 1 }}>
                Back
              </Button>
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            </Box>
          </StepContent>
        </Step>

        {/* Step 4: Preview & Save */}
        <Step>
          <StepLabel>Preview & Save</StepLabel>
          <StepContent>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Indicator Summary</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Code</Typography>
                    <Typography variant="body1" fontWeight="bold">{indicator.code}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body1">{indicator.name}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Description</Typography>
                    <Typography variant="body1">{indicator.description}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">Category</Typography>
                    <Chip label={indicator.category} color="primary" size="small" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">Data Type</Typography>
                    <Chip label={indicator.dataType} variant="outlined" size="small" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">Unit</Typography>
                    <Typography variant="body1">{indicator.unit}</Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>Data Sources ({indicator.mappings.length})</Typography>
                {indicator.mappings.map((mapping, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2">
                      {DATABASE_VIEWS.find(v => v.value === mapping.sourceTable)?.label} - {mapping.aggregation}({mapping.sourceField})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Grouped by: {mapping.groupBy.join(', ')}
                    </Typography>
                    {mapping.filters.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Filters: {mapping.filters.map(f => `${f.field} ${f.condition} ${f.value}`).join(', ')}
                      </Typography>
                    )}
                  </Box>
                ))}

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<PreviewIcon />}
                    onClick={() => setPreviewOpen(true)}
                  >
                    Preview SQL
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    color="success"
                  >
                    Save Indicator
                  </Button>
                </Box>
              </CardContent>
            </Card>

            <Box sx={{ mt: 2 }}>
              <Button onClick={handleBack} sx={{ mr: 1 }}>
                Back
              </Button>
            </Box>
          </StepContent>
        </Step>
      </Stepper>

      {/* SQL Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Generated SQL Query</DialogTitle>
        <DialogContent>
          <Paper sx={{ p: 2, backgroundColor: 'grey.100' }}>
            <pre style={{ fontFamily: 'monospace', fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
              {generateSQL()}
            </pre>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IndicatorBuilder;